/**
 * This is a compiled JS file.
 * Changes here could be overwritten. 
 * Contact your Shopify developers if changes need to be made.
 */

class CollectionKitRow {
  /**
   * @param {object} itemEl - a DOM node
   * @param {string} key - they key that should be updated in state
   */
  constructor(rowElement, key, type) {
    if (!rowElement) {
      return;
    }

    this.el = rowElement;

    this.key = key || 'collectionKit';
    this.type = type;
    this.collectionKit = window.WS.getStateByKey(this.key);

    // Set initial product values
    this.variant = this.getValues();
    this.setUIValues();

    // Update state to trigger add to cart bar
    window.WS.updateState(
      'UPDATE',
      { ...this.variant, persist: false },
      this.key
    );

    // If Sparklayer is enabled
    if (window.spark && window.sparkOptions) {
      this.getSparklayerVariant();
    }

    // Add event listeners
    this.onChange = this.handleOnChange.bind(this);

    this.el.addEventListener('change', this.onChange);

    this.onQuantityError = this.handleError.bind(this);

    document.body.addEventListener(
      `collectionKitQuantityError_${this.type}_${this.el.dataset.variantId}`,
      this.onQuantityError
    );

    this.onAddToCartError = this.handleError.bind(this);

    document.body.addEventListener(
      `addToCartError_${this.el.dataset.variantId}`,
      this.onAddToCartError
    );

    document.body.addEventListener(
      `addToCartError_${this.el.dataset.variantSku}`,
      this.onAddToCartError
    );

    this.onAddToCartSuccess = this.handleAddToCartSuccess.bind(this);

    document.body.addEventListener(
      `addToCartSuccess_${this.el.dataset.variantId}`,
      this.onAddToCartSuccess
    );

    document.body.addEventListener(
      `addToCartSuccess_${this.el.dataset.variantSku}`,
      this.onAddToCartSuccess
    );

    this.onCollectionKitReset = this.handleReset.bind(this);

    document.body.addEventListener(
      `collectionKitReset`,
      this.onCollectionKitReset
    );

    this.onSparklayerCalculatePrice = this.getSparklayerVariant.bind(this);

    document.body.addEventListener(
      'sparklayer_calculate_price',
      this.onSparklayerCalculatePrice
    );
  }

  getUIElements() {
    return {
      checkboxEl: this.el.querySelector('input[type="checkbox"]'),
      quantityEl: this.el.querySelector('input[name="quantity"]'),
      errorEl: this.el.querySelector('[data-variant-error]'),
      resetButtonEl: document.querySelector(`[data-kit-reset-button]`),
      plusButtonEl: this.el.querySelector('button[name="plus"]'),
      minusButtonEl: this.el.querySelector('button[name="minus"]'),
    };
  }

  /**
   * Make call to sparklayer to get variant data
   */
  getSparklayerVariant() {
    try {
      // Lookup product in Sparklayer to get trade variants (if any)
      window.spark
        .getProduct(this.el.dataset.productId)
        .then((product) => {
          if (product) {
            const variant = product.variants.find((variant) => {
              // Check the parent products sparklayer variants
              // Available products will have variant.price
              return (
                variant.sku === this.el.dataset.variantSku && variant.price
              );
            });

            if (variant) {
              this.updateVariant('sparklayer', { ...variant });

              // Check sparklayer settings for minOrderQuantity,
              // to update the kitQuantity
              // Kit Quantity should ideally never be less than Min Order Quantity, when set on a variant
              // so this is here to catch that.
              const sparklayerSettings = sparklayerHelper.parseSettings(
                this.variant
              );
              if (sparklayerSettings && sparklayerSettings.minOrderQuantity) {
                if (
                  Number(this.variant.quantity) <
                  sparklayerSettings.minOrderQuantity.value
                ) {
                  this.updateVariant(
                    'kitQuantity',
                    sparklayerSettings.minOrderQuantity.value.toString()
                  );
                }
              }

              this.setUIValues();

              // Update trade price in UI
              sparklayerHelper.renderTradePrice(
                this.variant,
                sparklayerHelper.getQuantitiesAccrossVariantsFromState(
                  `${this.key}.${this.type}_${this.el.dataset.variantId}`
                )
              );

              // Set new price in state (for add to cart bar)
              sparklayerHelper.calculatePriceAndUpdateState(
                this.key,
                this.variant,
                sparklayerHelper.getQuantitiesAccrossVariantsFromState(
                  `${this.key}.${this.type}_${this.el.dataset.variantId}`
                )
              );
            } else {
              // No Sparklayer variant found
              this.updateVariant('quantity', '0');
              this.updateVariant('kitQuantity', '0');
              this.setUIValues();

              // Update quantity
              window.WS.updateState(
                'UPDATE',
                { ...this.variant, persist: false },
                this.key
              );

              sparklayerHelper.renderPOA(this.el.dataset.variantSku);
            }
          }
        })
        .catch(() => {
          sparklayerHelper.renderPOA(this.el.dataset.variantSku);
        });
    } catch (e) {
      sparklayerHelper.renderPOA(this.el.dataset.variantSku);
    }
  }

  /**
   * Set product values from state or set defaults
   *
   * @returns {object} product
   */
  getValues(reset = false) {
    try {
      const id = this.el.dataset && this.el.dataset.variantId;
      const parentId = this.el.dataset && this.el.dataset.productId;
      const price = this.el.dataset && this.el.dataset.variantPrice;
      const kitQuantity = this.el.dataset && this.el.dataset.variantKitQuantity;
      const inventoryQuantity = this.el.dataset && this.el.dataset.variantInventoryQuantity;
      const inventoryPolicy = this.el.dataset && this.el.dataset.variantInventoryPolicy;
      const sku = this.el.dataset && this.el.dataset.variantSku;

      if (id === undefined) {
        // variant-id needed to tie the UI with items in state
        throw new Error('No item variant-id attribute');
      }

      if (price === undefined) {
        // variant-id needed to tie the UI with items in state
        throw new Error('No item variant-id attribute');
      }

      // Default values
      let product = {
        id: `${this.type}_${id}`,
        quantity: kitQuantity,
        parentId,
        kitQuantity,
        inventoryQuantity,
        price,
        sku,
      };

      const parsedInventoryQuantity = Number(inventoryQuantity);

      if (reset) {
        // If there is no product in stock
        if (parsedInventoryQuantity === 0) {
          if (this.type === 'core') {
            document.body.dispatchEvent(
              new CustomEvent(`collectionKitQuantityError_${product.id}`, {
                detail: {
                  message: `Package requires ${kitQuantity} but ${parsedInventoryQuantity} are available`,
                },
              })
            );
          }
        }

        if (window.spark && window.sparkOptions) {
          this.getSparklayerVariant();
        }

        return product;
      }

      // Check kit quantity against inventory
      if (
        inventoryPolicy != 'continue' &&
        (kitQuantity > parsedInventoryQuantity || parsedInventoryQuantity === 0)
      ) {
        if (this.type === 'core') {
          document.body.dispatchEvent(
            new CustomEvent(`collectionKitQuantityError_${product.id}`, {
              detail: {
                message: `Package requires ${kitQuantity} but ${parsedInventoryQuantity} are available`,
              },
            })
          );
        }

        // Set the quantity to the inventory quantity
        return {
          ...product,
          quantity: inventoryQuantity.toString(),
        };
      }

      return product;
    } catch (err) {
      console.error(err);
      return;
    }
  }

  /**
   * Update a specific key value on this.variant
   *
   * @param {string} key - key of the property to update
   * @param {(number|string|array)} value - a value to assign
   */
  updateVariant(key, value) {
    this.variant = {
      ...this.variant,
      [key]: value,
    };

    const { resetButtonEl } = this.getUIElements();

    if (this.variant.quantity !== this.variant.kitQuantity) {
      if (this.type === 'core' && resetButtonEl) {
        resetButtonEl.removeAttribute('disabled');
        document.body.dispatchEvent(
          new CustomEvent(`collectionKitQuantityError_${this.variant.id}`, {
            detail: {
              message: `The default quantity for this package is ${this.variant.kitQuantity}. Please check your changes are compatible.`,
            },
          })
        );
      }
    } else {
      this.handleClearError();
    }
  }

  /**
   * Set UI values using this.variant
   */
  setUIValues() {
    try {
      const id = this.variant && this.variant.id;

      if (id === undefined) {
        // Product id is needed to get the right item out of state
        throw new Error('No product id');
      }

      // Get the desired elements from getUIElements()
      const { checkboxEl, quantityEl, plusButtonEl, minusButtonEl } =
        this.getUIElements();

      if (checkboxEl) {
        checkboxEl.checked = this.variant.quantity !== '0';
      }

      if (quantityEl && plusButtonEl && minusButtonEl) {
        // Minus enable/disable
        if (Number(this.variant.quantity) > 0) {
          quantityEl.value = this.variant.quantity;
          minusButtonEl.removeAttribute('disabled');
        } else if (quantityEl) {
          quantityEl.value = '0';
          minusButtonEl.setAttribute('disabled', 'disabled');
        }

        const inventoryPolicy = quantityEl.dataset.inventoryPolicy;

        // Plus enable/disable
        // Check if max is a value
        if (Number(quantityEl.max) > 0 && inventoryPolicy !== 'continue') {
          if (Number(this.variant.quantity) === Number(quantityEl.max)) {
            plusButtonEl.setAttribute('disabled', 'disabled');
          } else if (Number(this.variant.quantity) < Number(quantityEl.max)) {
            plusButtonEl.removeAttribute('disabled');
          }
        }
      }

      // Check for Sparklayer settings
      if (window.spark && window.sparkOptions) {
        const sparklayerSettings = sparklayerHelper.parseSettings(this.variant);
        if (sparklayerSettings && sparklayerSettings.minOrderQuantity) {
          if (
            Number(this.variant.quantity) <
            sparklayerSettings.minOrderQuantity.value
          ) {
            sparklayerHelper.renderQuantityMessage(
              this.variant.sku,
              `Min. order quantity: ${sparklayerSettings.minOrderQuantity.value}`,
              'min-quantity'
            );
          }
        }

        if (sparklayerSettings && sparklayerSettings.maxOrderQuantity) {
          if (
            Number(this.variant.quantity) <
            sparklayerSettings.maxOrderQuantity.value
          ) {
            sparklayerHelper.renderQuantityMessage(
              this.variant.sku,
              `Max. order quantity: ${sparklayerSettings.maxOrderQuantity.value}`,
              'max-quantity'
            );

            quantityEl.setAttribute(
              'max',
              sparklayerSettings.maxOrderQuantity.value
            );
          }
        }
      }
    } catch (err) {
      console.error(err);
      return;
    }
  }

  /**
   * Resets product values, sets UI valuea
   * then calls state to keep things in sync
   */
  resetProductAndUI() {
    this.variant = this.getValues(true);

    this.setUIValues();
    this.handleClearError();

    window.WS.updateState(
      'UPDATE',
      { ...this.variant, persist: false },
      this.key
    );
  }

  /**
   * Check the values on the event object of the element
   * that has just changed
   *
   * @param {object} event
   */
  handleOnChange({ target: { type, value, checked } }) {
    const previousQuantity = this.variant.quantity;

    const { quantityEl } = this.getUIElements();

    if (type === 'checkbox') {
      if (!checked) {
        if (quantityEl) {
          quantityEl.setAttribute('value', 0);
          quantityEl.setAttribute('min', 0);
        }

        window.WS.updateState(
          'REMOVE',
          { ...this.variant, persist: false },
          this.key
        );

        this.updateVariant('quantity', '0');
        this.setUIValues();

        if (window.spark && window.sparkOptions) {
          this.getSparklayerVariant();

          document.body.dispatchEvent(
            new Event(`sparklayer_calculate_price`, {
              bubbles: true,
            })
          );
        }

        return; // exit early
      } else {
        if (Number(this.variant.quantity) === 0) {

          this.updateVariant('quantity', value);

          if (window.spark && window.sparkOptions) {
            const sparklayerSettings = sparklayerHelper.parseSettings(
              this.variant
            );

            if (sparklayerSettings && sparklayerSettings.minOrderQuantity) {
              // Allow the user do decrement below the minOrderQuantity
              // If they add to basket, Sparkayer will adjust
              if (
                Number(this.variant.quantity) < previousQuantity &&
                Number(this.variant.quantity) <
                  sparklayerSettings.minOrderQuantity.value
              ) {
                this.updateVariant('quantity', '0');
                // Othwewise, set the value to the minOrderQuantity
              } else if (
                Number(this.variant.quantity) <
                sparklayerSettings.minOrderQuantity.value
              ) {
                this.updateVariant(
                  'quantity',
                  sparklayerSettings.minOrderQuantity.value
                );
              }
            }
          } else {
            if (Number(quantityEl.min) === 0) {
              this.updateVariant('quantity', '1');
            } else {
              this.updateVariant('quantity', quantityEl.min);
            }
          }
        }

        this.setUIValues();
      }
    }

    if (type === 'number') {
      if (window.spark && window.sparkOptions) {
        const sparklayerSettings = sparklayerHelper.parseSettings(this.variant);
        const inventoryPolicy = quantityEl.dataset.inventoryPolicy;

        if (inventoryPolicy === 'continue') {
          if (
            Number(value) > Number(quantityEl.dataset.inventoryMax) &&
            Number(quantityEl.dataset.inventoryMax) > 0
          ) {
            document.body.dispatchEvent(
              new CustomEvent(`addToCartError_${this.el.dataset.variantId}`, {
                detail: {
                  message: `${
                    quantityEl.dataset.inventoryMax
                  } available. The additional ${
                    Number(value) - Number(quantityEl.dataset.inventoryMax)
                  } will be placed on backorder. ${quantityEl.dataset.restockDate}`,
                },
              })
            );
          } else {
            this.handleClearError();
          }
        }

        if (sparklayerSettings && sparklayerSettings.minOrderQuantity) {
          // Allow the user do decrement below the minOrderQuantity
          // If they add to basket, Sparkayer will adjust
          if (
            Number(this.variant.quantity) < previousQuantity &&
            Number(this.variant.quantity) <
              sparklayerSettings.minOrderQuantity.value
          ) {
            this.updateVariant('quantity', '0');
            // Othwewise, set the value to the minOrderQuantity
          } else if (
            Number(this.variant.quantity) <
            sparklayerSettings.minOrderQuantity.value
          ) {
            this.updateVariant(
              'quantity',
              sparklayerSettings.minOrderQuantity.value
            );
          }
        } else {
          this.updateVariant('quantity', value);
        }
      } else {
        // Check if max has a value
        if (Number(quantityEl.max) > 0) {
          this.updateVariant(
            'quantity',
            Number(value) > Number(quantityEl.max) ? quantityEl.max : value
          );
        } else {
          this.updateVariant('quantity', value);
        }
      }

      this.setUIValues();
    }

    window.WS.updateState(
      'UPDATE',
      { ...this.variant, persist: false },
      this.key
    );

    const { checkboxEl } = this.getUIElements();

    if (
      this.variant.variantId !== null &&
      Number(this.variant.quantity) > 0 &&
      checkboxEl
    ) {
      window.WS.updateState('UPDATE', this.variant, this.key);
      checkboxEl.removeAttribute('disabled');
    } else if (
      this.variant.variantId !== null &&
      Number(this.variant.quantity) === 0
    ) {
      window.WS.updateState(
        'REMOVE',
        { ...this.variant, persist: false },
        this.key
      );
    }

    if (window.spark && window.sparkOptions) {
      this.getSparklayerVariant();

      document.body.dispatchEvent(
        new Event(`sparklayer_calculate_price`, {
          bubbles: true,
        })
      );
    }
  }

  handleError(e) {
    const error = e && e.detail && e.detail.message;
    const { errorEl } = this.getUIElements();
    if (!error || !errorEl) { return }

    // Check to see if an error of the same type is already active...
    const activeError = this.el.querySelector(`[data-error-type="${e.type}"]`);

    if (activeError) {
      // If it is, we'll use the already-existing element and update its inner text.
      activeError.innerText = error;
    } else {
      // If it not, we'll create a new element and inject it into the page's markup.
      let errorInnerEl = document.createElement('span');
      errorInnerEl.innerText = error;
      errorInnerEl.setAttribute('data-error-type', e.type);
      errorEl.insertAdjacentElement('beforeend', errorInnerEl);
    }
  }

  handleClearError() {
    const { errorEl } = this.getUIElements();
    if (errorEl) {
      errorEl.innerHTML = '';
    }
  }

  handleAddToCartSuccess(e) {
    this.resetProductAndUI();

    this.handleClearError();

    const { resetButtonEl } = this.getUIElements();

    if (this.type === 'core' && resetButtonEl) {
      resetButtonEl.removeAttribute('disabled');
    }
  }

  handleReset() {
    this.resetProductAndUI();
  }
}

/**
 * Wrapper to initialise CollectionKitRow for product variants
 */
class CollectionKit extends HTMLElement {
  constructor() {
    super();

    const collectionId =
      (window.collectionKit && window.collectionKit.id) || 'collectionKit';

    this.resetKitButton = document.querySelector(`[data-kit-reset-button]`);
    this.resetKitButton?.setAttribute('disabled', 'true');

    document.querySelectorAll('[data-collection-kit-row]').forEach((item) => {
      // Initialise a new class on each collection kit product,
      // so it can manage it's UI and send/recieve state
      // from <page-state>
      new CollectionKitRow(item, `collectionKit_${collectionId}`, 'core');
    });

    document
      .querySelectorAll('[data-collection-kit-row-extra]')
      .forEach((item) => {
        // Initialise a new class on each collection kit extra product,
        // so it can manage it's UI and send/recieve state
        // from <page-state>
        new CollectionKitRow(item, `collectionKit_${collectionId}`, 'extra');
      });

    this.resetKitButton?.addEventListener('click', (e) => {
      e.preventDefault();

      document.body.dispatchEvent(new CustomEvent(`collectionKitReset`, {}));

      this.resetKitButton.setAttribute('disabled', 'true');
    }); 

    this.displayDefaultPrice();
  }

  displayDefaultPrice() {
    const price = this.querySelector('[data-collection-kit-default-price]');
    const priceDestination = this.querySelector('[data-collection-kit-default-price-destination]');

    if (!price || !priceDestination) { return; }

    priceDestination.innerHTML = price.innerHTML;
  }
}

customElements.define('collection-kit', CollectionKit);
